import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  VAT_MODE_STANDARD,
  VAT_MODE_OPTIONS,
  VAT_MODE_DEFAULT_LEGAL,
  isVatZero,
} from "@/lib/vatModes";

/**
 * Selettore "Regime IVA" condiviso fra Preventivo e Proforma.
 * - dropdown con 5 categorie + custom
 * - quando la modalità ≠ standard mostra un textarea con la dicitura legale
 *   (precompilata dal default mappato sul mode), modificabile dall'utente
 * - quando la modalità ≠ standard, la prop `onChange` riceve anche
 *   `vat_legal_text` impostato al default per evitare salvataggi vuoti
 *
 * Props:
 *   - form: oggetto form con `vat_mode` e `vat_legal_text`
 *   - onChange: ({ vat_mode, vat_legal_text }) => void
 *   - disabled: booleano
 *   - testIdPrefix: stringa per i data-testid (es. "quote", "pf")
 */
export function VatModeSelector({ form, onChange, disabled, testIdPrefix = "doc" }) {
  const mode = form.vat_mode || VAT_MODE_STANDARD;
  const legal = form.vat_legal_text || "";
  const showLegal = isVatZero(mode);

  const handleModeChange = (next) => {
    const defaultLegal = VAT_MODE_DEFAULT_LEGAL[next] || "";
    // Quando l'utente cambia modalità, popoliamo automaticamente il testo legale
    // con il default mappato (a meno che non avesse già scritto qualcosa di
    // diverso dal default precedente).
    const prevDefault = VAT_MODE_DEFAULT_LEGAL[mode] || "";
    const keepUserText = legal && legal.trim() && legal.trim() !== prevDefault.trim();
    onChange({
      vat_mode: next,
      vat_legal_text: keepUserText ? legal : defaultLegal,
    });
  };

  const handleLegalChange = (text) => {
    onChange({ vat_mode: mode, vat_legal_text: text });
  };

  return (
    <div className="card-surface p-5 space-y-3" data-testid={`${testIdPrefix}-vat-mode-card`}>
      <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">
        Regime IVA
      </h2>
      <div className="space-y-2">
        <Label className="text-xs text-zinc-500">Modalità</Label>
        <Select
          value={mode}
          onValueChange={handleModeChange}
          disabled={disabled}
        >
          <SelectTrigger
            className="rounded-sm"
            data-testid={`${testIdPrefix}-vat-mode-select`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VAT_MODE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showLegal && (
        <div className="space-y-2" data-testid={`${testIdPrefix}-vat-legal-wrapper`}>
          <Label className="text-xs text-zinc-500">
            Dicitura legale (mostrata in PDF)
          </Label>
          <textarea
            value={legal}
            onChange={(e) => handleLegalChange(e.target.value)}
            disabled={disabled}
            rows={2}
            className="input-field min-h-[60px] resize-y"
            placeholder="Es: Inversione contabile ex art. 17 c.6 lett. a) DPR 633/72"
            data-testid={`${testIdPrefix}-vat-legal-text`}
          />
          <p className="text-[11px] text-zinc-400 leading-snug">
            Quando il regime non è standard, l'IVA è forzata a 0% e il PDF riporta
            la dicitura sopra al posto della riga IVA.
          </p>
        </div>
      )}
    </div>
  );
}
